import { useEffect, useRef } from 'react';
import { Box, Group, Text } from '@mantine/core';

interface IInfoRowProps {
  label: string;
  value?: React.ReactNode;
  clamp?: number;
  tooltip?: boolean;
  expanded?: boolean;
  onTruncationChange?: (isTruncated: boolean) => void;
}

const isEmptyValue = (value?: React.ReactNode): boolean =>
  value == null || (typeof value === 'string' && !value.trim()) || (typeof value === 'number' && Number.isNaN(value));

export default function InfoRow(props: IInfoRowProps) {
  const { label, value, clamp = 2, expanded = false, onTruncationChange } = props;
  const displayValue = isEmptyValue(value) ? '-' : value;
  const isTextValue = typeof displayValue === 'string' || typeof displayValue === 'number';
  const valueTextRef = useRef<HTMLParagraphElement | null>(null);
  const lastTruncationRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isTextValue) {
      if (lastTruncationRef.current !== false) {
        lastTruncationRef.current = false;

        onTruncationChange?.(false);
      }

      return;
    }

    if (expanded) {
      return;
    }

    const element = valueTextRef.current;

    if (!element) {
      return;
    }

    const measureTruncation = () => {
      const nextIsTruncated = element.scrollHeight > element.clientHeight;

      if (lastTruncationRef.current === nextIsTruncated) {
        return;
      }

      lastTruncationRef.current = nextIsTruncated;

      onTruncationChange?.(nextIsTruncated);
    };

    const frameId = requestAnimationFrame(measureTruncation);

    const resizeObserver = new ResizeObserver(measureTruncation);

    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(frameId);

      resizeObserver.disconnect();
    };
  }, [clamp, displayValue, expanded, isTextValue, onTruncationChange]);

  const valueNode = isTextValue ? (
    <Text
      ref={valueTextRef}
      size="sm"
      c={displayValue === '-' ? 'dimmed' : undefined}
      lineClamp={expanded ? undefined : clamp}
      style={{
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
      }}
    >
      {displayValue}
    </Text>
  ) : (
    displayValue
  );

  return (
    <Group align="flex-start" gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
      <Text size="sm" c="dimmed" fw={500} style={{ minWidth: 72, whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      <Box
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >
        {valueNode}
      </Box>
    </Group>
  );
}
